import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineLeftCircle, AiOutlineRightCircle } from 'react-icons/ai';
import styled from 'styled-components';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import useAxios from '../../hooks/useAxios';
import { IGetTutorClass } from '../../models/tutor/tutorClass.model';
import { getTutorClass } from '../../api/tutor/tutorClass.api';
import Loader from '../../components/Loader';
import ErrorResponse from '../../components/ErrorResponse';
import { IGetNotices } from '../../models/boardNotice.model';
import { getNotices } from '../../api/boardNotice.api';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import ClassTable from '../../components/tutor/ClassTable';
import NoticeTable from '../../components/tutor/NoticeTable';
import ELearningTable from '../../components/tutor/ELearningTable';
import { Helmet } from 'react-helmet';
import { IGetFeedbackTasks, IGetTutorClasses } from '../../models/tutor.model';
import { getFeedbackTasks, getTutorClasses } from '../../api/tutor.api';
import { useLocation } from 'react-router-dom';
import QueryString from 'qs';
import PortfolioTable from '../../components/tutor/PortfolioTable';
import _ from 'lodash';
import { IGetCurrentSemester } from '../../models/semesters.model';
import { getCurrentSemester } from '../../api/semesters.api';

const Block = styled.div`
  .arrow > * {
    font-size: 26px;
    line-height: 26px;
  }

  .area-wrap {
    height: 370px;
  }

  .table-wrap {
    height: 307px;
    margin-bottom: 10px;
    overflow-y: auto;
  }
`;

const TutorDashboard: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { auth } = useSelector((root: RootState) => root.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { data: res, isLoading, error, execute } = useAxios<IGetTutorClass>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetNotices>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetFeedbackTasks>();
  const { data: res4, isLoading: isLoading4, error: error4, execute: execute4 } = useAxios<IGetTutorClasses>();
  const { data: res5, isLoading: isLoading5, error: error5, execute: execute5 } = useAxios<IGetCurrentSemester>();

  const [currentDate, setCurrentDate] = useState<Moment>();
  const [dates, setDates] = useState<string[]>([]);

  useEffect(() => {
    if (currentDate) {
      execute(getTutorClass({ srtDate: currentDate.format('YYYYMMDD') }));
    }
  }, [currentDate]);

  useEffect(() => {
    auth &&
      execute2(
        getNotices({
          perPage: 5,
          pageNo: 1,
          groupCode: auth.groupNo,
        }),
      );
  }, [auth]);

  useEffect(() => {
    execute4(getTutorClasses());
    execute5(getCurrentSemester());
  }, []);
  
  useEffect(() => {
    if (query.date) {
      setCurrentDate(moment(String(query.date), 'YYYYMMDD'));
    } else if (serverDate) {
      setCurrentDate(moment(serverDate));
    }
  }, [location, serverDate]);

  useEffect(() => {
    if (res4 && !isLoading4) {
      let dates: string[] = [];
      res4.data.forEach(item => {
        dates.push(item.timetableDate);
      });
      dates = _.uniq(dates);
      setDates(dates);
    }
  }, [res4, isLoading4]);

  useEffect(() => {
    if (res5 && !isLoading5) {
      doGetFeedbackTasks();
    }
  }, [res5, isLoading5]);

  const onCurrentDateChange = (date: Moment | null) =>
    date && setCurrentDate(date);

  const prevCurrentDate = () => {
    if (currentDate)
      setCurrentDate(currentDate.clone().subtract(1, 'days'));
  }
  const nextCurrentDate = () => {
    if (currentDate)
      setCurrentDate(currentDate.clone().add(1, 'days'));
  }

  const doGetFeedbackTasks = () => {
    if (!res5?.data?.semesterCode) return;

    execute3(getFeedbackTasks({
      evaluationSttCode: ['SB'],
      semesterCode: res5.data.semesterCode,
    }));
  }

  return (
    <Block className="content scrollbar">
      {(isLoading || isLoading2 || isLoading3 || isLoading4 || isLoading5) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={error4} />
      <ErrorResponse error={error5} />
      <Helmet>
        <title>{t('class.tutor.title1')}</title>
      </Helmet>

      <div className="mx-3 flex mt-5">
        <div className="col-12 text-center">
          <h5>
            <span className="arrow mr-3 pointer">
              <AiOutlineLeftCircle onClick={prevCurrentDate} />
            </span>
            <div className="date-picker__area">
              <DatePicker
                onChange={onCurrentDateChange}
                value={currentDate}
                disabledDate={current => !dates.includes(current.format('YYYYMMDD'))}
              />
            </div>
            <span className="arrow ml-3 pointer">
              <AiOutlineRightCircle onClick={nextCurrentDate} />
            </span>
          </h5>
        </div>
      </div>
      <div className="p-3 d-flex flex-row">
        <div className="col-6">
          <div className="area-wrap">
            <ClassTable
              data={res?.data ?? []}
              date={currentDate ? currentDate.format('YYYYMMDD') : moment(serverDate).format('YYYYMMDD')}
            />
          </div>
          <NoticeTable
            data={res2?.data ?? []}
          />
        </div>
        <div className="col-6">
          <div className="area-wrap">
            <ELearningTable
              data={res3?.data ?? []}
              doGetFeedbackTasks={doGetFeedbackTasks}
            />
          </div>
          <PortfolioTable
            semesterCode={res5?.data?.semesterCode}
          />
        </div>
      </div>
    </Block>
  );
};

export default TutorDashboard;
